import React, { useEffect, useState } from 'react';
import useDragTranslate from '../hooks/useDragTranslate';
import { getRandomWindowPosition } from '../utils/windowPositioning';
/* experience detail window component */

const ExperienceDetailWindow = ({ onClose, zIndex, onFocus, experienceData }) => {
  const { elRef, onPointerDown, onPointerMove, onPointerUp } = useDragTranslate(onFocus);
  /* drag functionality for this window */
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  /* slideshow state */
  
  // Generate random position for this window
  const [windowPosition] = useState(() => getRandomWindowPosition(900, 900));
  
  const handleFocus = () => {
    onFocus && onFocus();
    /* bring window to front when clicked */
  };

  const handleClose = () => {
    // 1) add .closing
    elRef.current?.classList.add('closing');
    /* start the close animation */

    // 2) after animation end, call onClose to unmount
    const done = () => {
      elRef.current?.removeEventListener('animationend', done);
      onClose();
    };
    elRef.current?.addEventListener('animationend', done, { once: true });
    /* wait for animation to finish then actually close */
  };

  const nextSlide = () => {
    const samples = experienceData?.sections?.SAMPLES?.SLIDESHOW?.IMAGES;
    if (samples) {
      setCurrentSlide((prev) => (prev + 1) % samples.length);
    }
  };

  const prevSlide = () => {
    const samples = experienceData?.sections?.SAMPLES?.SLIDESHOW?.IMAGES;
    if (samples) {
      setCurrentSlide((prev) => (prev - 1 + samples.length) % samples.length);
    }
  };
  /* slideshow navigation functions */

  useEffect(() => {
    // listen on the document so we can move outside the window bounds
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    /* set up drag event listeners */
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      /* clean up listeners */
    };
  }, [onPointerMove, onPointerUp]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 770);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div
      ref={elRef}
      className="window"
      data-window="experience-detail"
      style={{
        display: 'block',
        width: '1200px',
        height: '900px',
        zIndex: zIndex,
        position: 'fixed',
        '--tx': `${windowPosition.tx}px`,
        '--ty': `${windowPosition.ty}px`,
        // important: do not set left/top/transform here; css handles it.
      }}
      onClick={handleFocus}
    >
          <div className="title-bar draggable" onPointerDown={onPointerDown}>
            <div className="title-bar-text">{experienceData?.title || 'Experience Details'}</div>
            <div className="title-bar-controls">
              <button aria-label="Close" onClick={handleClose}></button>
            </div>
          </div>
      {/* window title bar with close button */}
      
      <div className="window-body experience-detail-body">
        <div className="experience-header">
          {isMobile && (
            <div className="experience-header-icons">
              {experienceData?.title === 'INTERDEPENDENT - Virtual Movie Studio' && (
                <>
                  <a href="https://www.interdependent.studio/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/googleIcon.png" alt="INTERDEPENDENT Website" className="experience-header-icon" />
                  </a>
                  <a href="https://www.linkedin.com/company/interdependent/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/linkedPixel.svg" alt="INTERDEPENDENT LinkedIn" className="experience-header-icon" />
                  </a>
                </>
              )}
              {experienceData?.title === 'Quest 2 Recovery - Medical Billing' && (
                <a href="https://quest2recovery.com" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                  <img src="/assets/googleIcon.png" alt="Quest 2 Recovery Website" className="experience-header-icon" />
                </a>
              )}
              {experienceData?.title === 'Visa - Payment Technology' && (
                <a href="https://visa.com" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                  <img src="/assets/googleIcon.png" alt="Visa Website" className="experience-header-icon" />
                </a>
              )}
              {experienceData?.title === 'Ignite Roleplay - Multiplayer Gaming Server' && (
                <>
                  <a href="https://www.dexerto.com/gta/what-is-igniterp-gta-rp-server-2130683/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/paperpixel.png" alt="Dexerto Article" className="experience-header-icon" />
                  </a>
                  <a href="https://www.igniteroleplay.net/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/googleIcon.png" alt="Ignite Roleplay Website" className="experience-header-icon" />
                  </a>
                  <a href="https://www.youtube.com/@Ignite_Roleplay" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/youtubePixel.png" alt="Ignite Roleplay YouTube" className="experience-header-icon" />
                  </a>
                  <a href="https://www.tiktok.com/@thebobbybuilds/video/7218721982064413995" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/tiktokPixel.png" alt="Ignite Roleplay TikTok" className="experience-header-icon" />
                  </a>
                  <a href="https://www.reddit.com/r/igniterp/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/redditpixel.png" alt="Ignite Roleplay Reddit" className="experience-header-icon" />
                  </a>
                  <a href="https://discord.com/invite/igniterp" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/discordpixel.png" alt="Ignite Roleplay Discord" className="experience-header-icon" />
                  </a>
                </>
              )}
            </div>
          )}
          
          <div className="experience-title-container">
            <h1 className="experience-title">
              {experienceData?.title}
              {!isMobile && experienceData?.title === 'Ignite Roleplay - Multiplayer Gaming Server' && (
                <a href="https://www.dexerto.com/gta/what-is-igniterp-gta-rp-server-2130683/" target="_blank" rel="noopener noreferrer" className="title-icon-link">
                  <img src="/assets/paperpixel.png" alt="Dexerto Article" className="title-icon" />
                </a>
              )}
            </h1>
            {!isMobile && (
              <div className="experience-header-icons">
                {experienceData?.title === 'INTERDEPENDENT - Virtual Movie Studio' && (
                  <>
                    <a href="https://www.interdependent.studio/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/googleIcon.png" alt="INTERDEPENDENT Website" className="experience-header-icon" />
                    </a>
                    <a href="https://www.linkedin.com/company/interdependent/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/linkedPixel.svg" alt="INTERDEPENDENT LinkedIn" className="experience-header-icon" />
                    </a>
                  </>
                )}
                {experienceData?.title === 'Quest 2 Recovery - Medical Billing' && (
                  <a href="https://quest2recovery.com" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/googleIcon.png" alt="Quest 2 Recovery Website" className="experience-header-icon" />
                  </a>
                )}
                {experienceData?.title === 'Visa - Payment Technology' && (
                  <a href="https://visa.com" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                    <img src="/assets/googleIcon.png" alt="Visa Website" className="experience-header-icon" />
                  </a>
                )}
                {experienceData?.title === 'Ignite Roleplay - Multiplayer Gaming Server' && (
                  <>
                    <a href="https://www.igniteroleplay.net/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/googleIcon.png" alt="Ignite Roleplay Website" className="experience-header-icon" />
                    </a>
                    <a href="https://www.youtube.com/@Ignite_Roleplay" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/youtubePixel.png" alt="Ignite Roleplay YouTube" className="experience-header-icon" />
                    </a>
                    <a href="https://www.tiktok.com/@thebobbybuilds/video/7218721982064413995" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/tiktokPixel.png" alt="Ignite Roleplay TikTok" className="experience-header-icon" />
                    </a>
                    <a href="https://www.reddit.com/r/igniterp/" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/redditpixel.png" alt="Ignite Roleplay Reddit" className="experience-header-icon" />
                    </a>
                    <a href="https://discord.com/invite/igniterp" target="_blank" rel="noopener noreferrer" className="experience-header-link">
                      <img src="/assets/discordpixel.png" alt="Ignite Roleplay Discord" className="experience-header-icon" />
                    </a>
                  </>
                )}
              </div>
            )}
          </div>
          <h2 className="experience-subtitle">{experienceData?.subtitle}</h2>
        </div>
        {/* experience header with title and subtitle */}

        <div className="experience-content">
          {experienceData?.sections && Object.entries(experienceData.sections).map(([key, section], index) => (
            <div key={index} className="experience-section">
              <h3 className="section-title" dangerouslySetInnerHTML={{ __html: section.TITLE }}></h3>
              <div className="section-content">
                {key === 'SAMPLES' && section.SLIDESHOW ? (
                    <div className={`slideshow-container ${experienceData?.company === 'INTERDEPENDENT' ? 'interdependent-gallery' : ''}`}>
                      <div className={`slideshow-image-container ${experienceData?.company === 'INTERDEPENDENT' ? 'interdependent-image-container' : ''}`} style={experienceData?.company === 'INTERDEPENDENT' ? {height: '650px'} : {}}>
                        {section.SLIDESHOW.IMAGES[currentSlide]?.type === 'youtube' ? (
                          <iframe 
                            src={section.SLIDESHOW.IMAGES[currentSlide]?.embedUrl}
                            width="560" 
                            height="315" 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                            referrerPolicy="strict-origin-when-cross-origin" 
                            allowFullScreen
                            className="slideshow-image youtube-iframe"
                          />
                        ) : section.SLIDESHOW.IMAGES[currentSlide]?.type === 'video' ? (
                          <video 
                            src={section.SLIDESHOW.IMAGES[currentSlide]?.src} 
                            controls
                            className="slideshow-image"
                          />
                        ) : (
                          <img 
                            src={section.SLIDESHOW.IMAGES[currentSlide]?.src} 
                            alt={section.SLIDESHOW.IMAGES[currentSlide]?.caption}
                            className="slideshow-image clickable-image"
                            onClick={() => window.open(section.SLIDESHOW.IMAGES[currentSlide]?.src, '_blank')}
                          />
                        )}
                      </div>
                      <div className="slideshow-caption">
                        <div dangerouslySetInnerHTML={{ __html: section.SLIDESHOW.IMAGES[currentSlide]?.caption || 'No caption available' }}></div>
                        {section.SLIDESHOW.IMAGES[currentSlide]?.link && (
                          <div className="slideshow-link">
                            <a href={section.SLIDESHOW.IMAGES[currentSlide]?.link} target="_blank" rel="noopener noreferrer">
                              🔗 View Link
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="slideshow-controls">
                        <button className="slideshow-arrow slideshow-prev" onClick={prevSlide}>
                          ‹
                        </button>
                        <div className="slideshow-indicator">
                          {currentSlide + 1} / {section.SLIDESHOW.IMAGES.length}
                        </div>
                        <button className="slideshow-arrow slideshow-next" onClick={nextSlide}>
                          ›
                        </button>
                      </div>
                    </div>
                  ) : (
                    section.CONTENT.map((paragraph, pIndex) => (
                      <p key={pIndex} className={`experience-paragraph ${paragraph.type || ''}`}>
                        {paragraph.text.includes('The Mailroom') ? (
                          <>
                            <span dangerouslySetInnerHTML={{ __html: paragraph.text.split('The Mailroom')[0] }}></span>
                            <a 
                              href="https://mailroom.interdependent.studio" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: '#0000ff', textDecoration: 'underline' }}
                            >
                              The Mailroom
                            </a>
                            <span dangerouslySetInnerHTML={{ __html: paragraph.text.split('The Mailroom')[1] }}></span>
                          </>
                        ) : (
                          <span dangerouslySetInnerHTML={{ __html: paragraph.text }}></span>
                        )}
                      </p>
                    ))
                  )}
              </div>
            </div>
          ))}
          {/* experience sections with different content types */}
        </div>
      </div>
    </div>
  );
};

export default ExperienceDetailWindow;
